import { Graphics } from 'pixi.js';
import { gsap } from 'gsap';
import type { AABB } from '../../engine/Physics';
import type { EnemyDef, GameConfig } from '../../types';
import type { Bullet } from '../Bullet';
import type { Player } from '../Player';

export interface EnemyContext {
  cfg: GameConfig;
  player: Player;
  solids: readonly AABB[];
  spawnBullet(b: Bullet): void;
  stepDt: number;
}

export abstract class Enemy {
  alive = true;
  hp: number;
  x: number;
  y: number;
  abstract width: number;
  abstract height: number;
  gfx = new Graphics();

  constructor(public def: EnemyDef) {
    this.x = def.x;
    this.y = def.y;
    this.hp = def.hp;
  }

  abstract step(ctx: EnemyContext): void;

  bounds(): AABB {
    return { x: this.x, y: this.y, w: this.width, h: this.height };
  }

  /** Returns true if killed by this hit. */
  hit(): boolean {
    if (!this.alive) return false;
    this.hp -= 1;
    gsap.killTweensOf(this.gfx.scale);
    gsap.fromTo(
      this.gfx.scale,
      { x: 1.3, y: 0.7 },
      { x: 1, y: 1, duration: 0.18, ease: 'back.out(2)' },
    );
    if (this.hp <= 0) {
      this.alive = false;
      gsap.to(this.gfx.scale, { x: 0, y: 0, duration: 0.25, ease: 'back.in(2)' });
      gsap.to(this.gfx, {
        rotation: Math.PI * 0.5,
        alpha: 0,
        duration: 0.25,
      });
      return true;
    }
    return false;
  }

  syncGfx(): void {
    this.gfx.x = this.x + this.width / 2;
    this.gfx.y = this.y + this.height / 2;
  }

  destroy(): void {
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy();
  }
}
